import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import Avatar from "@/components/Avatar";
import InviteModal from "@/components/InviteModal";
import ReliabilityPill from "@/components/ReliabilityPill";
import { firstName } from "@/lib/names";
import { formatDMY } from "@/lib/dates";
import { Radio, Send, Eye } from "lucide-react";

export default function BeaconTracker({ user, refreshKey, onInvited }) {
  const [beacons, setBeacons] = useState([]);
  const [inviteFor, setInviteFor] = useState(null);
  const invitePrefill = useMemo(() => ({ city: user?.city, day_rate: inviteFor?.day_rate }), [user?.city, inviteFor?.day_rate]);

  const load = useCallback(() => {
    api.get("/jobs/beacon/mine").then(({ data }) => setBeacons(data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    if (!beacons.length) return;
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [beacons.length, load]);

  if (!beacons.length) return null;
  return (
    <div className="mt-6 border border-white/10 bg-[#121212] p-5" data-testid="beacon-tracker">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-mono-data text-xs tracking-widest text-[#FF6A00]">// YOUR PINGS · LAST 7 DAYS</div>
          <div className="font-display font-black text-xl uppercase">Who's answered the flare</div>
        </div>
        <span className="text-[11px] text-zinc-500 font-mono-data">Updates every 20s</span>
      </div>
      <div className="mt-4 space-y-3">
        {beacons.map((b) => (
          <div key={b.id} className="border border-white/10 bg-black p-4" data-testid={`beacon-row-${b.id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-sm">
                <Radio className="w-4 h-4 text-[#FF6A00]" />
                <span className="text-white font-semibold">{b.trade}s</span>
                <span className="text-zinc-500">{b.city ? `· ${b.city}` : ""}{b.date ? ` · ${formatDMY(b.date)}` : ""} · sent {formatDMY(b.created_at)}</span>
              </div>
              <div className="flex items-center gap-3 font-mono-data text-xs">
                <span className="text-zinc-400"><Send className="inline w-3 h-3 mr-1" />{b.sent} pinged</span>
                <span className={b.opened ? "text-[#16A34A]" : "text-zinc-500"} data-testid={`beacon-opened-${b.id}`}><Eye className="inline w-3 h-3 mr-1" />{b.opened} opened</span>
              </div>
            </div>
            {b.responders.length > 0 ? (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                {b.responders.map((c) => (
                  <div key={c.id} className="flex items-center justify-between gap-3 border border-white/10 p-2" data-testid={`beacon-responder-${b.id}-${c.id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <Avatar src={c.photo_url} name={c.name} size={32} />
                      <div className="min-w-0">
                        <div className="text-sm text-white font-semibold truncate">{firstName(c.name)} <span className="text-zinc-500 font-normal">· {c.city}</span></div>
                        <div className="flex items-center gap-2 text-[11px] text-zinc-500 font-mono-data">${c.day_rate}/d · opened {formatDMY(c.opened_at)} <ReliabilityPill reliability={c.reliability} /></div>
                      </div>
                    </div>
                    <button onClick={() => setInviteFor(c)} className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 bg-[#FF6A00] text-black text-[10px] font-black uppercase tracking-widest hover:bg-white" data-testid={`beacon-invite-${b.id}-${c.id}`}>
                      <Send className="w-3 h-3" /> Invite
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-2 text-xs text-zinc-500">No one's opened it yet — tradies usually respond within the hour.</div>
            )}
          </div>
        ))}
      </div>
      <InviteModal open={!!inviteFor} onClose={() => setInviteFor(null)} contractor={inviteFor}
        prefill={invitePrefill}
        onCreated={() => { setInviteFor(null); onInvited?.(); }} />
    </div>
  );
}
